import "./CardDashedGraph.scss";

import { useMemo, useState, Fragment, useEffect } from "react";
import { Typography } from "../../ui";
import { Category, RechartsDashed } from "../";
import { prepareIndicators, setFilterData } from "../../modules/Company/helpers";
import { Box, MenuItem } from "@material-ui/core";
import { FormControl, InputLabel, Select } from "@mui/material";
import { useFormikContext } from "formik";

const LEGEND = [
  { key: "shows", name: "Показы", color: "#5dce5a", active: true },
  { key: "reach", name: "Охваты", color: "#eeb55d", active: false },
  { key: "clicks", name: "Клики", color: "#eeb55d", active: true },
  { key: "leads", name: "Лиды", color: "#eeb55d", active: false },
  { key: "spent", name: "Расход (₽)", color: "#8dbfea", active: false },
  { key: "ctr", name: "CTR %", color: "#a566d5", active: false },
  { key: "cpc", name: "CPC (₽)", color: "#f99d9d", active: false },
  { key: "ecpm", name: "eCPM ( ₽)", color: "#f4e75e", active: false },
  { key: "cr", name: "CR (%)", color: "#77797a", active: false },
  { key: "cpa", name: "CPA", color: "#cccdcc", active: false },
];

const CATEGORIES = [
  {
    type: "Показы",
    amount: (shows) => shows?.reduce((sum, val) => sum + val, 0) || 0,
    name: "shows",
    group: 1,
  },
  {
    type: "Охваты",
    amount: (reach) => reach?.reduce((sum, val) => sum + val, 0) || 0,
    name: "reach",
    group: 1,
  },
  {
    type: "Клики",
    amount: (clicks) => clicks?.reduce((sum, val) => sum + val, 0) || 0,
    name: "clicks",
    group: 1,
  },
  {
    type: "Лиды",
    amount: (leads) => leads?.reduce((sum, val) => sum + val, 0) || 0,
    name: "leads",
    group: 1,
  },
  {
    type: "Расход",
    amount: (spent) => (spent?.reduce((sum, val) => sum + val, 0) || 0)?.toFixed(2),
    name: "spent",
    group: 1,
  },
  {
    type: "CTR",
    amount: (ctr) => 
      ctr?.length ? `${(ctr.reduce((sum, val) => sum + val, 0) / ctr.length).toFixed(2)}%` : "0%",
    name: "ctr",
    group: 1,
  },
  {
    type: "CPC (₽)",
    amount: (cpc) => 
      cpc?.length ? (cpc.reduce((sum, val) => sum + val, 0) / cpc.length).toFixed(2) : 0,
    name: "cpc",
    group: 1,
  },
  {
    type: "eCPM (₽)",
    amount: (ecpm) => 
      ecpm?.length ? (ecpm.reduce((sum, val) => sum + val, 0) / ecpm.length).toFixed(2) : 0,
    name: "ecpm",
    group: 1,
  },
  {
    type: "CR (%)",
    amount: (cr) => 
      cr?.length ? (cr.reduce((sum, val) => sum + val, 0) / cr.length).toFixed(2) : 0,
    name: "cr",
    group: 1,
  },
  {
    type: "CPA",
    amount: (cpa) => (cpa?.reduce((sum, val) => sum + val, 0) || 0)?.toFixed(2),
    name: "cpa",
    group: 1,
  },
];

function CardDashedGraph({ title, chartData, categories = CATEGORIES, legend = LEGEND, type, userData, isMobile, hiddenMetricKeys = [] }) {
  const effectiveLegend = useMemo(() => (legend || LEGEND).filter(item => !hiddenMetricKeys?.includes(item.key)), [legend, hiddenMetricKeys]);
  const effectiveCategories = useMemo(() => (categories || CATEGORIES).filter(cat => !hiddenMetricKeys?.includes(cat.name)), [categories, hiddenMetricKeys]);
  const [indicators, setIndicators] = useState(() => prepareIndicators(effectiveLegend));
  const [date] = useState([]);
  const formik = useFormikContext();
  const filteredData = useMemo(
    () => setFilterData(date, chartData?.time_intervals),
    [date, chartData?.time_intervals]
  );

  useEffect(() => {
    setIndicators(prepareIndicators(effectiveLegend));
  }, [effectiveLegend]);

  const filteredIndicators = useMemo(
    () => effectiveLegend.filter(ind => indicators[ind.key]),
    [indicators, effectiveLegend]
  );

  const countIndicators = () => Object.values(indicators).filter(Boolean).length;

  const getActiveIndicators = () => 
    Object.keys(indicators).filter(key => indicators[key]);

  const isSecondItem = (name) => {
    const active = getActiveIndicators();
    return active.length > 1 && active[1] === name;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const [datePart] = dateStr.split('T');
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="CardDashedGraph">
      <div className="header">
        {title && (
          <Fragment>
            <div style={{ width: isMobile ? "100%" : "80%", display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", position: "relative" }}>
              <div className="title">
                <Typography variant="h2" color="dark-gray">
                  Общая статистика
                </Typography>
                {chartData?.time_intervals?.length > 0 && (
                  <Typography variant="body1" color="dark-gray">
                    Период ({formatDate(chartData.time_intervals[0]?.[0])} -{" "}
                    {formatDate(chartData.time_intervals.slice(-1)[0]?.[1])})
                  </Typography>
                )}
              </div>

              { userData.user.id !== "84149b79-02d9-4919-8a92-e2531594dc4b" && (
                <Box sx={{ width: 250, marginTop: isMobile ? "12px" : "0" }}>
                  <FormControl fullWidth size="small">
                    <InputLabel
                      id="ad-select-label"
                      sx={{ lineHeight: 1, opacity: "80%", fontSize: 14 }}
                    >
                      Тип графика
                    </InputLabel>
                    <Select
                      labelId="ad-select-label"
                      id="ad-select"
                      value={formik.values.graph_type}
                      onChange={(e) => {
                        formik.setFieldValue("graph_type", e.target.value);
                      }}
                      displayEmpty
                      style={{
                        height: 32,
                        background: "#FFFFFF",
                        borderColor: "transparent",
                      }}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "transparent",
                        },
                      }}
                    >
                      <MenuItem value={"campaigns"}>
                        <div style={{ padding: "10px 0" }}>По кампаниям</div>
                      </MenuItem>
                      <MenuItem value={"adverts"}>
                        <div style={{ padding: "10px 0" }}>По объявлениям</div>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ) }
            </div>
          </Fragment>
        )}
      </div>

      <div className="main-block">
        <div className="graph">
          {chartData?.data?.length > 0 ? (
            <RechartsDashed 
              type={type} 
              legend={filteredIndicators} 
              data={chartData} 
              userData={userData}
              isMobile={isMobile}
            />
          ) : (
            <div style={{ paddingLeft: isMobile ? "50px" : "0", paddingTop: isMobile ? "20px" : "0" }} className="no-data">Нет данных для отображения</div>
          )}
        </div>
        
        <div className="category-list">
          {effectiveCategories.map(({ type, amount, name }) => {
            const metricIndex = chartData.metrics?.indexOf(name) ?? -1;
            const values = filteredData?.map(interval => 
              metricIndex >= 0 ? interval[metricIndex] : 0
            ) || [];

            return (
              <Category
                key={type}
                current_item={isSecondItem(name) ? { key: "second" } : null}
                type={type}
                amount={amount(values)}
                checked={indicators[name]}
                disabled={!indicators[name] && countIndicators() > 1}
                handleClick={(value) => setIndicators(prev => ({
                  ...prev,
                  [name]: value
                }))}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CardDashedGraph;